import { Grid, Typography } from "@mui/material";
import {
  ScanningIndicator,
  useScanIndicator,
  useToast
} from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import { ScanningVerification } from "~/api/scanningVerifyMethod";
import { PickedToteWithCaller } from "~/api/toteTypes/pickedTote";
import { useAppDispatch, useAppSelector } from "~/app/store";
import { ASDSThemeProvider } from "~/components/designSystem/ASDSThemeProvider";

import { StartPickingModal } from "~/features/startPickingModal/StartPickingModal";
import { useBinStatusCheck } from "~/hooks/useBinStatusCheck";
import { debounce } from "~/hooks/useDebounce";
import { useDevCheats } from "~/hooks/useDevCheats";
import { useDisclosure } from "~/hooks/useDisclosure";
import { useFcNavigate } from "~/hooks/useFcNavigate";
import { useInactivityResetTimer } from "~/hooks/useInactivityResetTimer";
import { useNavbar, ViewNameTranslation } from "~/hooks/useNavbar";
import { usePortStatusWithReduxUpdates } from "~/hooks/usePortStatusWithReduxUpdates";
import { useBarcodeScanner, useKeyDownHandler } from "~/lib/barCodeScan";
import {
  ternaryIff,
  matchExactUpcsOrSkus,
  displayPickData,
  isSiteUsingShipments,
  getBarcodeValue,
  matchesUpc
} from "~/lib/helpers";
import { isFetchBaseQueryError } from "~/lib/isFetchBaseQueryError";
import {
  mixpanelTrack,
  mixpanelTrackPickWrapper,
  useMixpanelPageName
} from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { isAbortedRequest } from "~/lib/shared";
import { usePickSubscription, useToteSubscription } from "~/lib/signalr";
import usePromiseInterval from "~/lib/usePromiseIntervalEffect";
import {
  outOfStockPickAutostore,
  setPreviousAutostoreBin,
  setCartNumberConfirmed,
  setNextPickingBinLoading,
  setIsPickQuantityConfirmed,
  setPickedTote,
  setTotePickingStarted,
  noBatchesLeftMessage
} from "~/redux/actions";
import { fetchBatchWithCartNumber } from "~/redux/actions/batch";
import { fetchToteById } from "~/redux/actions/home";
import { clearUserMessage } from "~/redux/actions/site";
import { usePartiallyCompleteMutation } from "~/redux/public/picks.hooks";
import { StoreState } from "~/redux/reducers";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectUsersClientId } from "~/redux/selectors/authSelectors";
import {
  selectPickingStateArgs,
  getCurrentPick
} from "~/redux/selectors/autostoreSelectors";
import {
  selectAbleQtyToPick,
  selectOutOfStockModalStatus,
  selectOutOfStockReasonCode
} from "~/redux/selectors/outOfStockSelectors";
import {
  selectPickingScannedBarcode,
  selectScanAndApplyLabelModalActiveStep
} from "~/redux/selectors/pickingSelectors";
import { selectIsRecurringScheduleFc } from "~/redux/selectors/storeSelectors";
import {
  selectThisWorkstation,
  selectWorkstationAutostoreGridId,
  selectWorkstationAvailablePorts,
  selectWorkstationId
} from "~/redux/selectors/workstationsSelectors";
import {
  useGetFocusedPickQuery,
  useGetBatchPickingStateQuery,
  useGetTotesForBatchQuery,
  useNextPickBinMutation,
  useStartPickingOnWorkstationMutation
} from "~/redux/warehouse/autostorePicking.hooks";
import { useGetWaveProgressQuery } from "~/redux/warehouse/batches.hooks";
import { useLazyGetOrdersQuery } from "~/redux/warehouse/orders.hooks";
import {
  useCaptureWeightMutation,
  useGetCapturedWeightStateMutation,
  useLazyGetPickQuery,
  useRejectWeightMutation,
  useVerifyPickMutation
} from "~/redux/warehouse/pick.hooks";
import { useGetRecurringSchedulesQuery } from "~/redux/warehouse/recurringSchedule.hooks";
import { useScanLabelMutation } from "~/redux/warehouse/totes.hooks";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";
import {
  MeasuredValueDto,
  SignalRPickEventDto,
  ToteEventDto,
  WeightThresholdCheckResult
} from "~/types/api";

import AddToteModal from "./AddToteModal";
import { AutostorePickingTotes } from "./AutostorePickingTotes";
import FocusedPickInfoCard from "./FocusedPickInfoCard";
import PickingBins from "./PickingBins";
import RestockPickModal from "./RestockPickModal";
import TopActionButtons from "./TopActionButtons";
import { closeAddToteModal } from "./addToteModal.slice";
import ScanAndApplyLabelConfirm from "./applyLabelDialog/ScanAndApplyLabel";
import {
  incrementScanAndApplyModalActiveStep,
  resetScanAndApplyModalActiveStep
} from "./applyLabelDialog/scanAndApplyLabel.slice";
import { TotesPrepModal } from "./assignTotesDialog/TotesPrepModal";
import {
  openWeightEntryModal,
  resetWeightCaptureResponse,
  selectWeightThresholdCheckResult,
  selectWeightsCapturedOnPickSoFar,
  setCurrentBatchId,
  setCurrentPickQuantity,
  setPickCompartment
} from "./autostorePicking.slice";
import { CartNumberConfirmationModal } from "./cartNumberConfirmationModal/CartNumberConfirmationModal";
import { ConfirmPickQuantityModal } from "./confirmPickQuantityModal/ConfirmPickQuantityModal";
import { setPickedQuantityModalStatus } from "./confirmPickQuantityModal/confirmPickQuantityModal.slice";
import { useAddedTotes } from "./hooks/useAddedTotes";
import usePickingFunctionalities from "./hooks/usePickingFunctionalities";
import { useRefetchBatchPickingState } from "./hooks/useRefetchBatchPickingState";
import AutostoreOutOfStockDialog from "./outOfStockDialog/AutostoreOutOfStockDialog";
import {
  setAbleToPickQty,
  setOutOfStockDialogStatus
} from "./outOfStockDialog/outOfStockDialog.slice";
import { closeHoldBinModal } from "./problemSolveModal.slice";
import { openRestockPickModal } from "./restockPickModal.slice";
import {
  resetScannedBarcode,
  setScannedBarcode
} from "./slices/pickingScannedBarcode.slice";
/** True when every each for the pick has a captured weight within threshold. */
export function areAllPickWeightsCaptured({
  pickQty,
  weightsCapturedOnPickSoFar,
  weightThresholdCheckResult
}: {
  pickQty: number | undefined;
  weightsCapturedOnPickSoFar: MeasuredValueDto[];
  weightThresholdCheckResult: WeightThresholdCheckResult;
}): boolean {
  return (
    pickQty !== undefined &&
    weightsCapturedOnPickSoFar.length === pickQty &&
    weightThresholdCheckResult === "WithinThreshold"
  );
}

const mapStateToProps = (state: StoreState) => ({
  signalr: state.autostore.signalr,
  batchWithCartNumber: state.batch.batchWithCartNumber,
  pickingConfigurations:
    state.store.usersFulfillmentCenter?.pickingConfigurations || null,
  cartNumberConfirmed: state.autostore.cartNumberConfirmed,
  fulfillmentCenter: state.store.usersFulfillmentCenter,
  userMessages: state.site.userMessages,
  isPickQuantityConfirmed: state.autostore.isPickQuantityConfirmed,
  nextPickingStateLoading: state.autostore.nextPickingStateLoading,
  siteAllPortIds: state.workstations.siteAllPortIds,
  siteWorkstation: state.workstations.siteWorkstation,
  pickedTote: state.autostore.pickedTote,
  pickingStartedToteId: state.autostore.pickingStartedToteId,
  showTwoBinComponents: state.autostore.showTwoBinComponents,
  displayToteConfirmationModalForTote:
    state.autostore.displayToteConfirmationModalForTote
});

const connector = connect(mapStateToProps, {
  outOfStockPickAutostore,
  setPreviousAutostoreBin,
  fetchBatchWithCartNumber,
  setCartNumberConfirmed,
  setIsPickQuantityConfirmed,
  setPickedTote,
  setTotePickingStarted,
  clearUserMessage
});

type Props = ConnectedProps<typeof connector> & {
  viewTitle?: ViewNameTranslation;
};

export function AutostorePicking(props: Props) {
  // props
  const {
    batchWithCartNumber,
    pickingConfigurations,
    cartNumberConfirmed,
    fulfillmentCenter,
    userMessages,
    isPickQuantityConfirmed,
    nextPickingStateLoading,
    pickedTote,
    pickingStartedToteId,
    viewTitle,
    showTwoBinComponents,
    displayToteConfirmationModalForTote
  } = props;

  const {
    ap_totesPlacement: totesPlacement,
    ap_prepTotesModalEnabled: prepTotesModalEnabled,
    ap_confirmCartNumberEnabled,
    ap_fusionPortScreenEnabled,
    ap_scanningIndicatorShown,
    ap_applyingLabelProcessEnabled,
    ap_showOrderOrProductCancellationModal
  } = useAppSelector(selectServerConfig);
  const trackedPageName = useMixpanelPageName();

  // hooks
  const navigate = useFcNavigate();
  const { t } = useTranslation();
  const { errorToast, successToast, warningToast } = useToast();
  const dispatch = useAppDispatch();
  const { setInformationalMenuItems, setMenuItems } = useNavbar({
    isAutostorePicking: true,
    viewTitle
  });
  const [scanState, setScanState] = useScanIndicator();
  const binStatusCheck = useBinStatusCheck();
  const { originalTotes, addedTotes } = useAddedTotes();
  const { handleSlamProcess } = usePickingFunctionalities();
  const { restartInactivityTimer, clearInactivityTimer } =
    useInactivityResetTimer({});

  // selectors
  const clientId = useAppSelector(selectUsersClientId);
  const workstationId = useAppSelector(selectWorkstationId);
  const isRestockPickModalOpen = useAppSelector(
    (state) => state.restockPickModal.isOpen
  );
  const outOfStockModalStatus = useAppSelector(selectOutOfStockModalStatus);
  const outOfStockReasonCode = useAppSelector(selectOutOfStockReasonCode);
  const weightsCapturedOnPickSoFar = useAppSelector(
    selectWeightsCapturedOnPickSoFar
  );
  const weightThresholdCheckResult = useAppSelector(
    selectWeightThresholdCheckResult
  );
  const isAddToteModalOpen = useAppSelector(
    (state) => state.addToteModal.isOpen
  );
  const ableToPickQty = useAppSelector(selectAbleQtyToPick);
  const {
    open: batchCompletedModalOpen,
    onOpen: onBatchCompleteModalOpen,
    onClose: onBatchCompleteModalClose
  } = useDisclosure();
  const scannedBarcode = useAppSelector(selectPickingScannedBarcode);
  const confirmPickQuantityModalIsShown = useAppSelector(
    (state) => state.confirmPickQuantityModalSlice.isShown
  );
  const problemSolveModalOpen = useAppSelector(
    (state) => state.problemSolveModal.isOpen
  );
  const isRecurringScheduleFc = useAppSelector(selectIsRecurringScheduleFc);
  const gridId = useAppSelector(selectWorkstationAutostoreGridId);
  const deviceId = useAppSelector(selectThisWorkstation)?.deviceId ?? "";
  const [portId] = useAppSelector(selectWorkstationAvailablePorts);
  const scanAndApplyLabelModalActiveStep = useAppSelector(
    selectScanAndApplyLabelModalActiveStep
  );

  const portStatus = usePortStatusWithReduxUpdates({ portId });

  const isReady = portStatus.isReady;
  const isBinOpen = portStatus.isBinOpen;
  const isBinReadyForScan = isReady || isBinOpen;

  const pickingStateArgs = useAppSelector(selectPickingStateArgs);
  const {
    data: pickingState,
    isFetching: pickingStateLoading,
    currentPick
  } = useGetBatchPickingStateQuery(pickingStateArgs, {
    pollingInterval: props.signalr.state === "Disconnected" ? 2000 : 0,
    selectFromResult: (result) => ({
      ...result,
      currentPick: getCurrentPick(result.data)
    })
  });
  const refetchPickingState = useRefetchBatchPickingState();

  // general polling for focused pick and totes for batch until signalr is implemented. this should
  // prevent us from staying in stale state for too long.
  const { data: focusedPick } = useGetFocusedPickQuery(
    workstationId ? { workstationId } : skipToken,
    { pollingInterval: 7000 }
  );

  const focusedPickUpcs = focusedPick?.allUpcs;
  const focusedPickSku = focusedPick?.sku;
  const focusedPickScanCandidates: string[] = [
    ...(focusedPickUpcs ?? []),
    ...(focusedPickSku ? [focusedPickSku] : [])
  ];
  const focusedPickQty = focusedPick?.quantity.value;
  const sourceBinId = focusedPick?.autostoreBinId;
  const isWeightedPick = !!focusedPick?.weightThreshold;

  useGetTotesForBatchQuery(
    focusedPick ? { batchId: focusedPick.batchId } : skipToken,
    { pollingInterval: 7000 }
  );

  // useState
  const [recentBatchInfo, setRecentBatchInfo] = useState<{
    batchId?: Guid;
    batchName?: string;
    temperatureZone?: string;
  } | null>(null);

  useDevCheats({ showAutostoreStatus: true });

  const [isHandleMoveNextPickCalled, setIsHandleMoveNextPickCalled] =
    useState(false);

  // useRef
  const lastPickIdRef = useRef<string | null>(null);
  const isHandleMoveNextPickCalledTimer = useRef(0);
  const lastPickedTote = useRef<PickedToteWithCaller>({
    pickedTote: null,
    caller: null
  });

  const nextPickingBinCallerTimestamp = useRef<number | null>(null);

  const { areShipmentsEnabled } = useGetRecurringSchedulesQuery(undefined, {
    skip: !isRecurringScheduleFc,
    selectFromResult: (result) => ({
      areShipmentsEnabled: isSiteUsingShipments(result.data)
    })
  });

  const { data: _ } = useGetWorkstationsQuery(undefined, {
    refetchOnMountOrArgChange: true
  });

  const {
    refetch: refetchGetWaveProgress,
    isError: isWaveProgressBarError,
    error: waveProgressBarError
  } = useGetWaveProgressQuery(
    pickingState && areShipmentsEnabled
      ? { batchId: pickingState.batchId }
      : skipToken,
    {
      refetchOnMountOrArgChange: true
    }
  );

  const [fetchPick] = useLazyGetPickQuery();
  const [fetchOrder] = useLazyGetOrdersQuery();
  const [captureWeight] = useCaptureWeightMutation();
  const [rejectWeight, { isLoading: isRejectingWeight }] =
    useRejectWeightMutation();
  const [getCapturedWeightState, { isLoading: isGettingCapturedWeightState }] =
    useGetCapturedWeightStateMutation();
  const isRemovingWeight = isRejectingWeight || isGettingCapturedWeightState;
  const pickQtyForWeights = ableToPickQty ?? focusedPickQty;
  const arePickWeightsReady =
    !isWeightedPick ||
    (areAllPickWeightsCaptured({
      pickQty: pickQtyForWeights,
      weightsCapturedOnPickSoFar,
      weightThresholdCheckResult
    }) &&
      !isRemovingWeight);

  const [verifyPick] = useVerifyPickMutation();
  const [scanLabel] = useScanLabelMutation();
  const [partiallyComplete] = usePartiallyCompleteMutation();
  const [fetchNextPickBin, fetchNextBinResponse] = useNextPickBinMutation();
  const [startPickingOnWorkstation] = useStartPickingOnWorkstationMutation();

  const totePositionsConfirmed = pickingState?.totes.reduce((acc, tote) => {
    const isConfirmed = [
      "Position Confirmed",
      "Picked",
      "Picking",
      "Canceled"
    ].includes(tote.status);
    return isConfirmed && acc;
  }, true);

  const shouldDisplayToteConfirmationModalForSpecificTote = () => {
    if (pickingState && displayToteConfirmationModalForTote) {
      const toteInCurrentBatch = pickingState.totes.find(
        (tote) => tote.toteId === displayToteConfirmationModalForTote.toteId
      );
      return !!toteInCurrentBatch;
    }

    return false;
  };

  const displayToteConfirmationModalForSpecificTote =
    shouldDisplayToteConfirmationModalForSpecificTote();
  const displayTotesPrepModal =
    prepTotesModalEnabled &&
    !batchCompletedModalOpen &&
    pickingState &&
    pickingState.totes.length &&
    (!totePositionsConfirmed || displayToteConfirmationModalForSpecificTote);

  // Property which indicate if Scan Label Modal should be displayed after all picks from tote are completed
  const isScanLabelModalHidden = ap_applyingLabelProcessEnabled
    ? !pickedTote
    : true;

  const isScanAndApplyLabelModalShown =
    !isScanLabelModalHidden && !outOfStockModalStatus;

  const isPickQuantityConfirmationFlowEnabled =
    fulfillmentCenter?.pickQuantityConfirmationEnabled &&
    !!focusedPick &&
    focusedPick?.quantity.value > 1 &&
    ((prepTotesModalEnabled && totePositionsConfirmed) ||
      (ap_confirmCartNumberEnabled && cartNumberConfirmed));

  const isCartNumberConfirmationShown =
    ap_confirmCartNumberEnabled && !cartNumberConfirmed;

  const handleFetchNextPickBin = async () => {
    if (!pickingState || !gridId || !portId) return;

    const autostoreBinId = currentPick?.pickBin?.autostoreBin?.autostoreBinId;

    const autostoreBinGuid = currentPick?.pickBin?.binId;

    let allPicksComplete;
    let nextBinWasSuccessful = false;

    // We need to call update picking state before fetching next picking bin also
    // Because we can end up in scenario where we for example
    // Have two orders and if we cancel currently non active order
    // And complete pick from active order, our picking state wouldn't be updated
    // So our tote progress bar wouldn't show proper color of canceled picks
    const { data: updatedPickingState } = await refetchPickingState();
    let newCurrentPick = currentPick;
    // change the current pick
    if (updatedPickingState && updatedPickingState.currentPicks.length) {
      // current pick comes from picking state and is assumed to be the first pick with the highest quantity in the current picks array
      newCurrentPick =
        updatedPickingState?.currentPicks.reduce(
          (max, pick) =>
            pick?.quantity?.value > max?.quantity?.value ? pick : max,
          pickingState.currentPicks[0]
        ) || null;
      // or fetch next pick bin, then
    } else {
      try {
        const fetchNextPickBinResponse = await fetchNextPickBin({
          portId,
          gridId,
          workstationId
        }).unwrap();

        allPicksComplete = fetchNextPickBinResponse.allPicksComplete;
        nextBinWasSuccessful = true;
      } catch (err) {
        const message = getMessageFromRtkError(err);
        if (
          message ===
          "There is nothing left to pick or no appropriate bin found."
        ) {
          warningToast(noBatchesLeftMessage);
        } else {
          errorToast(message);
        }
      }

      if (!nextBinWasSuccessful) return;

      if (allPicksComplete) {
        // When the last tote in the batch is completed, we have two paths for updating state related to the SLAM modal:
        // 1. Update state which will show the SLAM modal when the all picks are completed - This one is expected here
        // 2. Update state which will show the SLAM modal when the 'tote picked' event is received
        if (
          ap_applyingLabelProcessEnabled &&
          !lastPickedTote.current.caller &&
          lastPickedTote.current.pickedTote &&
          lastPickedTote.current.pickedTote.toteId
        ) {
          lastPickedTote.current.caller = "all picks completed";
          const pickedToteDto = await dispatch(
            fetchToteById(lastPickedTote.current.pickedTote.toteId)
          );
          await handleSlamProcess(
            lastPickedTote.current.pickedTote.toteId,
            lastPickedTote.current.pickedTote.orderId,
            pickedToteDto
          );
        }
        onBatchCompleteModalOpen();
        dispatch(setOutOfStockDialogStatus(false));
      }
    }
    props.setPreviousAutostoreBin({
      binId: autostoreBinId,
      binGuid: autostoreBinGuid,
      autostoreCompartmentNumber:
        newCurrentPick?.pickBin?.autostoreCompartmentNumber || 1,
      inventoryId: newCurrentPick?.inventoryId
    });
  };

  const resetAfterPick = () => {
    dispatch(setCurrentPickQuantity(0));
    if (fulfillmentCenter?.pickQuantityConfirmationEnabled) {
      props.setIsPickQuantityConfirmed(false);
      dispatch(resetScannedBarcode());
    }
  };

  const handleMoveNextPick = debounce(async (args: { reporter: string }) => {
    const { reporter } = args;

    if (
      (nextPickingStateLoading &&
        reporter.toLowerCase() !== "added tote click") ||
      batchCompletedModalOpen
    ) {
      return;
    }

    // Check the timestamp difference between the last two calls to this function
    // If the difference is less than a second, just ignore the second call
    const callerTimestamp = new Date().getTime();
    if (
      nextPickingBinCallerTimestamp.current &&
      Math.abs(callerTimestamp - nextPickingBinCallerTimestamp.current) < 1000
    ) {
      nextPickingBinCallerTimestamp.current = callerTimestamp;
      return;
    }
    nextPickingBinCallerTimestamp.current = callerTimestamp;

    resetAfterPick();
    await handleFetchNextPickBin();
    dispatch(setNextPickingBinLoading(false));
  }, 500);

  const resetStateAndRedirect = useCallback(
    (callback?: () => void) => {
      // If fulfillment center pickQuantityConfirmationEnabled property is enabled, we want to reset modal state
      if (
        fulfillmentCenter &&
        fulfillmentCenter.pickQuantityConfirmationEnabled
      ) {
        dispatch(
          setPickedQuantityModalStatus({
            isShown: false,
            pickedQuantity: null,
            selectedBinId: null
          })
        );
      }

      if (ap_applyingLabelProcessEnabled) {
        if (pickedTote) {
          dispatch(setPickedTote(null));
        }
        if (pickingStartedToteId) {
          dispatch(setTotePickingStarted(null));
        }
      }
      if (fulfillmentCenter?.pickQuantityConfirmationEnabled) {
        dispatch(setIsPickQuantityConfirmed(false));
        dispatch(resetScannedBarcode());
      }
      if (callback) {
        callback();
      }
    },
    [
      ap_applyingLabelProcessEnabled,
      dispatch,
      fulfillmentCenter,
      pickedTote,
      pickingStartedToteId
    ]
  );

  const handleToteClick = useCallback(
    async (args: {
      forbidHandleMoveNextPickCall?: boolean;
      reporter?: string;
    }) => {
      const { forbidHandleMoveNextPickCall, reporter } = args;

      dispatch(setNextPickingBinLoading(true));

      if (!isHandleMoveNextPickCalled) {
        setIsHandleMoveNextPickCalled(true);
      }
      try {
        if (!focusedPick || !gridId || !clientId || !fulfillmentCenter) return;

        const pickId = focusedPick.pickId;
        const binId = focusedPick.binId;
        const pickQty = ableToPickQty || focusedPick.quantity.value;

        await partiallyComplete({
          pickId,
          clientId,
          fulfillmentCenterId: fulfillmentCenter.fulfillmentCenterId,
          requestBody: {
            autostoreGridId: gridId,
            holdReasonCode: outOfStockReasonCode,
            quantity: pickQty,
            pickBinId: binId,
            workstationId
          }
        }).unwrap();

        dispatch(resetWeightCaptureResponse());

        if (!forbidHandleMoveNextPickCall) {
          await handleMoveNextPick({ reporter: reporter || "tote click" });
        } else {
          dispatch(setNextPickingBinLoading(false));
        }
      } catch (err) {
        errorToast(getMessageFromRtkError(err));
        dispatch(setNextPickingBinLoading(false));
      }

      // When the last pick in the tote is completed, we have two paths for updating state related to the SLAM modal:
      // 1. Update state which will show the SLAM modal when the last pick is confirmed from the Qubit - This one is expected here
      // 2. Update state which will show the SLAM modal when the 'tote picked' event is received
      if (
        ap_applyingLabelProcessEnabled &&
        !lastPickedTote.current.caller &&
        lastPickedTote.current.pickedTote &&
        lastPickedTote.current.pickedTote.toteId
      ) {
        lastPickedTote.current.caller = "confirm pick";
        const pickedToteDto = await dispatch(
          fetchToteById(lastPickedTote.current.pickedTote.toteId)
        );
        await handleSlamProcess(
          lastPickedTote.current.pickedTote.toteId,
          lastPickedTote.current.pickedTote.orderId,
          pickedToteDto
        );
      }
    },
    [
      isHandleMoveNextPickCalled,
      setIsHandleMoveNextPickCalled,
      focusedPick,
      gridId,
      clientId,
      fulfillmentCenter,
      ableToPickQty,
      outOfStockReasonCode,
      workstationId,
      partiallyComplete,
      handleMoveNextPick,
      errorToast,
      ap_applyingLabelProcessEnabled,
      handleSlamProcess,
      dispatch
    ]
  );

  // polling 4:  restart isHandleMoveNextPickCalled state
  // conditions: next-picking-bin already triggered from handleToteClick
  // time:       every second
  usePromiseInterval(
    () => {
      if (isHandleMoveNextPickCalledTimer.current > 6) {
        setIsHandleMoveNextPickCalled(false);
        isHandleMoveNextPickCalledTimer.current = 0;
      } else {
        isHandleMoveNextPickCalledTimer.current += 1;
      }
      return Promise.resolve();
    },
    1000,
    isHandleMoveNextPickCalled
  );

  // picking state change
  useEffect(() => {
    if (!pickingState) return;
    if (pickingStateLoading) return;
    if (!currentPick) return;

    const currentPickId = currentPick.pickId;
    const isNewPick = lastPickIdRef.current !== currentPickId;

    if (isNewPick) {
      // We have moved to a different pick
      lastPickIdRef.current = currentPickId;

      // Reset quantity for the new pick
      dispatch(setCurrentPickQuantity(currentPick.quantity.value || 0));

      // Clear any "Item Missing" quantity from the previous pick
      if (ableToPickQty != null) {
        dispatch(setAbleToPickQty(null));
      }
    }

    // This stuff should still run on any pickingState change
    setRecentBatchInfo({
      batchId: pickingState.batchId,
      batchName: pickingState.batchName,
      temperatureZone: currentPick.pickBin?.temperatureZone
    });

    const autostoreCompartmentNumber =
      currentPick.pickBin?.autostoreCompartmentNumber || 1;

    dispatch(setPickCompartment(autostoreCompartmentNumber - 1 || 0));

    // We want to trace info about the last pick in the tote before the tote is
    // fully picked. We set all necessary info about picked tote. We set caller
    // property to be null because we want to update that info once the caller
    // is decided (either the 'tote picked' event or the 'confirm pick' method)
    void (async () => {
      if (ap_applyingLabelProcessEnabled) {
        const scheduledPicks = pickingState.allPicks
          .filter(
            (tote) =>
              tote.toteId === pickingState.currentPicks[0]?.assignedToteId
          )
          .map((tote) => tote.picks)
          .flat()
          .filter((pick) => pick.status.toLowerCase() === "scheduled");

        // If there is only one scheduled pick, that's the last pick in the tote
        if (scheduledPicks.length === 1) {
          const pickInfo = await fetchPick(scheduledPicks[0].pickId).unwrap();
          if (pickInfo) {
            lastPickedTote.current = {
              pickedTote: {
                pickId: pickInfo.pickId,
                toteId: pickInfo.assignedToteId || pickInfo.originalToteId,
                orderId: pickInfo.orderId
              },
              caller: null
            };
          }
        } else {
          lastPickedTote.current = {
            pickedTote: null,
            caller: null
          };
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickingState, pickingStateLoading]);

  // fetch batch for cart number if cart is not confirmed
  useEffect(() => {
    if (!pickingState) return;
    if (
      ap_confirmCartNumberEnabled &&
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      !cartNumberConfirmed &&
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      !batchWithCartNumber
    ) {
      void props.fetchBatchWithCartNumber(pickingState.batchName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartNumberConfirmed, pickingState]);

  // handle "the pick was not in the scheduled status" messages by checking if
  // the pick belongs to a canceled order.
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
    const pickWasNotInScheduledStatusMessageExists = userMessages.find(
      (message) =>
        message.title?.toLowerCase().trim() ===
        "the pick was not in the scheduled status"
    );
    void (async () => {
      // if 'The pick was not in the scheduled status' message appears
      // we need to check if this pick belongs to canceled order
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      if (pickWasNotInScheduledStatusMessageExists && currentPick) {
        const currentPickUpdated = await fetchPick(currentPick.pickId).unwrap();
        if (
          currentPickUpdated &&
          currentPickUpdated.status.toLowerCase() === "canceled"
        ) {
          await refetchPickingState();
          if (currentPick) {
            const orderInfo = (
              await fetchOrder({
                orderId: currentPick.orderId
              }).unwrap()
            ).at(0);

            if (
              orderInfo &&
              orderInfo.status.toLowerCase() === "canceled" &&
              ap_showOrderOrProductCancellationModal
            ) {
              dispatch(openRestockPickModal());
            } else {
              await handleMoveNextPick({
                reporter: "userMessages useEffect"
              });
            }
            props.clearUserMessage(pickWasNotInScheduledStatusMessageExists.id);
          }
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userMessages.length]);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (fulfillmentCenter && !fulfillmentCenter.pickQuantityConfirmationEnabled)
      return;

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if ((displayTotesPrepModal || outOfStockModalStatus) && scannedBarcode)
      dispatch(resetScannedBarcode());

    // if all weights have not been captured (ie user rejects a weight), ensure the pick isn't confirmed

    if (!arePickWeightsReady) props.setIsPickQuantityConfirmed(false);

    if (
      arePickWeightsReady &&
      !!scannedBarcode &&
      focusedPickScanCandidates.length &&
      focusedPickQty &&
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      !displayTotesPrepModal &&
      !outOfStockModalStatus &&
      matchExactUpcsOrSkus(focusedPickScanCandidates, scannedBarcode) &&
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      isScanLabelModalHidden &&
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      !isPickQuantityConfirmed
    ) {
      dispatch(
        setPickedQuantityModalStatus({
          isShown: true,
          pickedQuantity: focusedPickQty,
          selectedBinId: sourceBinId || null
        })
      );
      dispatch(resetScannedBarcode());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannedBarcode, outOfStockModalStatus, arePickWeightsReady]);

  // This useEffect covers situation when we do partial oos on first pick and
  // there is no inventory for rest of the picks in the batch. In that case, we
  // should display scan label for that partially oos tote and after label is
  // scanned and applied, we should close the port end navigate to
  // autostore-main page.
  useEffect(() => {
    if (
      ap_applyingLabelProcessEnabled &&
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      isScanLabelModalHidden &&
      fetchNextBinResponse.isError &&
      getMessageFromRtkError(fetchNextBinResponse.error) ===
        "There is nothing left to pick or no appropriate bin found." &&
      !batchCompletedModalOpen
    ) {
      resetStateAndRedirect(() => {
        successToast("Port successfully closed.");
        navigate("/autostore-main");
      });
    }
  }, [
    isScanLabelModalHidden,
    fetchNextBinResponse,
    ap_applyingLabelProcessEnabled,
    batchCompletedModalOpen,
    navigate,
    resetStateAndRedirect,
    successToast
  ]);

  // In case that Qubit ends up in a situation where the 'scan & apply label'
  // modal and the 'pick quantity confirmation' modal are displayed at the same
  // time. We want to close the 'pick quantity confirmation' modal immediately
  // and keep displaying only 'scan & apply label' modal.
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (isScanAndApplyLabelModalShown && confirmPickQuantityModalIsShown) {
      dispatch(
        setPickedQuantityModalStatus({
          isShown: false,
          pickedQuantity: null,
          selectedBinId: null
        })
      );
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      if (isPickQuantityConfirmed) {
        props.setIsPickQuantityConfirmed(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScanAndApplyLabelModalShown, confirmPickQuantityModalIsShown]);

  // If the pick canceled order is received while the OOS or Hold Bin modal is
  // open. We want only to show the Pick/Order cancellation modal since the user
  // can't OOS or put the inventory on hold anymore because the bin will go
  // away. Also if the Pick/Order cancellation modal is shown after the Pick
  // Confirmation modal is already displayed, we should close the Pick
  // Confirmation modal because the bin will go away and after that, the picker
  // should scan the product from the next bin and enter proper quantity.
  useEffect(() => {
    if (isRestockPickModalOpen && ap_showOrderOrProductCancellationModal) {
      if (outOfStockModalStatus) dispatch(setOutOfStockDialogStatus(false));
      if (problemSolveModalOpen) dispatch(closeHoldBinModal());
      if (
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        fulfillmentCenter?.pickQuantityConfirmationEnabled &&
        confirmPickQuantityModalIsShown
      )
        dispatch(
          setPickedQuantityModalStatus({
            isShown: false,
            pickedQuantity: null,
            selectedBinId: null
          })
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRestockPickModalOpen, ap_showOrderOrProductCancellationModal]);

  // If the totes prep modal is displayed, that means that a new batch has just
  // started. And there are no picked totes at the beginning of the picking
  // process so we can safely close the slam modal.
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (displayTotesPrepModal && ap_applyingLabelProcessEnabled && pickedTote) {
      props.setPickedTote(null);
      dispatch(resetScanAndApplyModalActiveStep());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ap_applyingLabelProcessEnabled, displayTotesPrepModal, pickedTote]);

  /* PICK EVENTS */
  // receive pick events (from lightning PTL presses)
  // pick events are received even when the pick comes from a tote click
  const pickSubscription = async (event: SignalRPickEventDto) => {
    if (!pickingState) return;
    if (
      ["completed", "outofstock"].includes(event.eventType.toLowerCase()) &&
      pickingState.batchId === event.batchId &&
      currentPick &&
      currentPick.pickId === event.pick.pickId
    ) {
      restartInactivityTimer();
      // If applying label process is enabled, we need to track which tote the
      // completed pick belongs to. When Add Tote functionality is called, we
      // will send this tote id as pickedToteId prop to ScanAndApplyLabelConfirmModal
      if (ap_applyingLabelProcessEnabled && event.pick.assignedToteId) {
        props.setTotePickingStarted(event.pick.assignedToteId);
      }

      // close 'Problem solve', 'Add tote' and/or 'Out of stock' modals if the
      // pick completed/out of stock event received
      if (problemSolveModalOpen) dispatch(closeHoldBinModal());
      if (isAddToteModalOpen) dispatch(closeAddToteModal());
      if (outOfStockModalStatus) dispatch(setOutOfStockDialogStatus(false));

      if (!isHandleMoveNextPickCalled) {
        // Pick was completed via PTL (not UI click)
        const totePosition = pickingState.totes.find(
          (t) => t.toteId === event.pick.assignedToteId
        )?.totePosition;
        mixpanelTrack({
          trackedPageName,
          type: "PTL",
          label: "Confirm Pick",
          eventProperties: {
            "Tote Position": totePosition,
            "Pick Id": event.pick.pickId,
            SKU: event.pick.sku,
            Quantity: event.pick.quantity?.value
          }
        });
        dispatch(setNextPickingBinLoading(true));
        await handleMoveNextPick({
          reporter: "pick event received"
        });
      }

      if (isHandleMoveNextPickCalled) {
        setIsHandleMoveNextPickCalled(false);
        isHandleMoveNextPickCalledTimer.current = 0;
      }
    }

    if (
      ["outofstock", "canceled"].includes(event.eventType.toLowerCase()) &&
      pickingState.batchId === event.batchId &&
      currentPick &&
      currentPick.pickId === event.pick.pickId
    ) {
      restartInactivityTimer();
      // If applying label process is enabled, we need to track which tote the
      // out of stocked pick belongs to. When Add Tote functionality is called,
      // we will send this tote id as pickedToteId prop to ScanAndApplyLabelConfirmModal
      if (ap_applyingLabelProcessEnabled && event.pick.assignedToteId) {
        props.setTotePickingStarted(event.pick.assignedToteId);
      }
    }

    if (
      event.eventType.toLowerCase() === "canceled" &&
      pickingState.batchId === event.batchId &&
      currentPick &&
      currentPick.pickId === event.pick.pickId
    ) {
      if (ap_showOrderOrProductCancellationModal)
        dispatch(openRestockPickModal());
    }
  };

  // Reset and rehydrate weight capture state when the focused pick changes
  // (e.g. page refresh or returning to a pick mid-capture).
  useEffect(() => {
    dispatch(resetWeightCaptureResponse());

    const pickId = focusedPick?.pickId;
    if (!pickId || !isWeightedPick) return;

    const request = getCapturedWeightState({ pickId });
    request.unwrap().catch((err: unknown) => {
      if (isAbortedRequest(err)) return;
      errorToast(getMessageFromRtkError(err));
    });

    return () => {
      // Abort in-flight checks on unmount so a slower response cannot overwrite a newer pick's weights
      request.abort();
    };
  }, [
    getCapturedWeightState,
    dispatch,
    errorToast,
    focusedPick?.pickId,
    isWeightedPick
  ]);

  const handleRemoveWeight = async (weightValue: number) => {
    if (isRemovingWeight) return;

    const weightsBeforeReject = weightsCapturedOnPickSoFar;
    const rejectedWeight = weightsBeforeReject.find(
      (weight) => weight.value === weightValue
    );
    if (!rejectedWeight || !focusedPick) return;

    try {
      await rejectWeight({
        pickId: focusedPick.pickId,
        rejectedWeight,
        reasonCode:
          weightThresholdCheckResult === "AboveMaximum"
            ? "OverThreshold"
            : weightThresholdCheckResult === "BelowMinimum"
              ? "UnderThreshold"
              : "NotSpecified",
        workstationId,
        ...(ableToPickQty !== null && {
          adjustedPickQuantity: {
            value: ableToPickQty,
            units: focusedPick.quantity.units
          }
        })
      }).unwrap();
    } catch (err) {
      errorToast(getMessageFromRtkError(err));
    }
  };

  usePickSubscription(pickSubscription);

  // For clients that are consuming SLAM process
  // Waiting for tote picked event (all picks from tote are completed) which
  // should indicate that AA needs to scan the label
  const toteSubscription = async (event: ToteEventDto) => {
    // When the last pick in the tote is completed, we have two paths for
    // updating state related to the SLAM modal:
    // 1. Update state which will show the SLAM modal when the last pick is
    // confirmed from the Qubit
    // 2. Update state which will show the SLAM modal when the 'tote picked'
    // event is received - This one is expected here
    if (
      event.eventType.toLowerCase() === "picked" &&
      (!lastPickedTote.current?.caller ||
        lastPickedTote.current.caller === "tote picked event" ||
        !lastPickedTote.current.pickedTote) &&
      ap_applyingLabelProcessEnabled
    ) {
      // Check if tote belongs to current batch
      const isPickedToteInPickingState = pickingState?.totes.find(
        (tote) => tote.toteId === event.tote.toteId
      );
      if (isPickedToteInPickingState) {
        lastPickedTote.current.caller = "tote picked event";

        // In this moment, Qubit needs updated information about Picked Tote picks
        await handleSlamProcess(
          event.tote.toteId,
          event.tote.orderId,
          event.tote,
          false,
          event.portId
        );
      }
    }

    // When added event received, we need to send parent tote id to indicate
    // proper tote for applying label
    if (event.eventType === "Added" && pickingStartedToteId) {
      // Check if tote belongs to current batch
      const { data: pickingStateLocal } = await refetchPickingState();
      if (!pickingStateLocal) return;

      const isPickedToteInPickingState = pickingState?.totes.find(
        (tote) => tote.toteId === pickingStartedToteId
      );
      // Ideally we should use gridId from the event but that value is always null
      const addedToteBelongsToCurrentBatch = pickingStateLocal?.totes.some(
        (tote) => tote.orderId === event.tote.orderId
      );
      if (
        pickingStateLocal &&
        isPickedToteInPickingState &&
        addedToteBelongsToCurrentBatch
      ) {
        const applyLabelTote = pickingStateLocal.totes.find(
          (tote) => tote.toteId === pickingStartedToteId
        );
        if (applyLabelTote) {
          await handleSlamProcess(
            applyLabelTote.toteId,
            applyLabelTote.orderId,
            applyLabelTote,
            false,
            event.portId
          );
        }
      }
    }
  };

  useToteSubscription(toteSubscription);

  const getNextBatch = async () => {
    try {
      let batchId: string | null = null;
      onBatchCompleteModalClose();
      if (ap_confirmCartNumberEnabled) {
        props.setCartNumberConfirmed(false);
      }
      if (nextPickingBinCallerTimestamp.current)
        nextPickingBinCallerTimestamp.current = null;
      if (!!gridId && !!portId && workstationId) {
        try {
          const chosenState = await startPickingOnWorkstation({
            gridId,
            workstationId,
            forceClosePort: false,
            enableMultiPort: ap_fusionPortScreenEnabled
              ? showTwoBinComponents || undefined
              : undefined,
            deviceId,
            temperatureZones: []
          }).unwrap();

          batchId = chosenState?.pickingState.batchId ?? null;
          if (batchId) dispatch(setCurrentBatchId(batchId));
        } catch (err) {
          const message = getMessageFromRtkError(err);
          if (
            message.includes(
              "There is nothing left to pick or no appropriate bin found."
            )
          ) {
            warningToast(noBatchesLeftMessage);
            if (!pickedTote) {
              resetStateAndRedirect(() => {
                successToast("Port successfully closed.");
                navigate("/autostore-main");
              });
            }
          } else {
            errorToast(message);
          }
        }
      }

      if (
        fulfillmentCenter &&
        fulfillmentCenter.pickQuantityConfirmationEnabled
      ) {
        dispatch(
          setPickedQuantityModalStatus({
            isShown: false,
            pickedQuantity: null,
            selectedBinId: null
          })
        );
      }
      if (ap_applyingLabelProcessEnabled) {
        if (pickedTote) {
          props.setPickedTote(null);
        }
        if (pickingStartedToteId) {
          props.setTotePickingStarted(null);
        }
        lastPickedTote.current = {
          pickedTote: null,
          caller: null
        };
      }

      return batchId;
    } catch {
      return null;
    }
  };

  const handleHomeButtonClick = () => {
    resetStateAndRedirect(() => {
      successToast("Port successfully closed.");
      onBatchCompleteModalClose();
      navigate("/autostore-main");
    });
  };

  useKeyDownHandler();
  useBarcodeScanner<boolean>({
    findScanMatch: async (initialBuffer: string) => {
      const buffer: string = getBarcodeValue(initialBuffer);
      dispatch(setScannedBarcode(buffer));
      const shouldSkipQuantityConfirmation =
        ableToPickQty === 1 || focusedPick?.quantity?.value === 1;

      restartInactivityTimer();

      // If 'Scan & Apply label' modal is shown, AA should scan the label (match with external tote id)
      if (!isScanLabelModalHidden && !outOfStockModalStatus) {
        const isProperLabelScanned =
          buffer === pickedTote?.externalToteId?.trim();
        const wrongLabelScannedMessage =
          "Scanned value does not match the label. Please scan the correct label";
        if (pickedTote && isProperLabelScanned) {
          const messageExits = userMessages.find(
            (message) => message.title === wrongLabelScannedMessage
          );
          if (messageExits) {
            props.clearUserMessage(messageExits.id);
          }
          mixpanelTrackPickWrapper(
            `${trackedPageName}: Scan and Apply Label Modal`,
            "Scan",
            "Scan Label",
            pickedTote.totePosition
          );
          setScanState("success");
          void scanLabel({
            toteId: pickedTote.toteId,
            autostorePortId: portId,
            autostoreGridId: gridId,
            workstationId
          });
          if (scanAndApplyLabelModalActiveStep === 0) {
            dispatch(incrementScanAndApplyModalActiveStep());
          }
        }

        if (!isProperLabelScanned) {
          errorToast(wrongLabelScannedMessage);
        }
      }

      if (
        !isBinReadyForScan ||
        !focusedPick ||
        !focusedPick.allUpcs ||
        !isScanLabelModalHidden ||
        isRemovingWeight ||
        displayTotesPrepModal
      )
        return false;

      if (isWeightedPick && !arePickWeightsReady) {
        try {
          const captureWeightResponse = await captureWeight({
            pickId: focusedPick.pickId,
            productCode: buffer,
            entryMethod: "Scanner",
            workstationId,
            ...(ableToPickQty !== null && {
              adjustedPickQuantity: {
                value: ableToPickQty,
                units: focusedPick.quantity.units
              }
            })
          }).unwrap();

          setScanState("success");

          const captureWeightState = captureWeightResponse.captureWeightState;

          const weightsReady = areAllPickWeightsCaptured({
            pickQty: pickQtyForWeights,
            weightsCapturedOnPickSoFar:
              captureWeightState.allWeightsCapturedOnPickSoFar,
            weightThresholdCheckResult:
              captureWeightState.weightThresholdCheckResult
          });
          if (!weightsReady) return true;
        } catch (err) {
          const message = getMessageFromRtkError(err);
          errorToast(message);
          if (
            isFetchBaseQueryError(err) &&
            message.toLowerCase().includes("weight is required")
          ) {
            dispatch(openWeightEntryModal());
          }
          return false;
        }
      }

      if (
        !!fulfillmentCenter &&
        fulfillmentCenter.pickQuantityConfirmationEnabled &&
        !!focusedPick.allUpcs &&
        matchesUpc(focusedPick.allUpcs, buffer)
      ) {
        const totePosition = pickingState?.totes.find(
          (t) => t.toteId === focusedPick.assignedToteId
        )?.totePosition;
        mixpanelTrack({
          trackedPageName,
          type: "Scan",
          label: "Confirm Pick",
          eventProperties: {
            "Tote Position": totePosition,
            "Pick Id": focusedPick.pickId,
            SKU: focusedPick.sku,
            Quantity: focusedPick.quantity?.value
          }
        });
        if (isScanLabelModalHidden) {
          setScanState("success");
        }
        if (
          shouldSkipQuantityConfirmation &&
          gridId &&
          portId &&
          !isScanAndApplyLabelModalShown
        ) {
          props.setIsPickQuantityConfirmed(true);
          dispatch(
            setPickedQuantityModalStatus({
              isShown: false,
              pickedQuantity: null,
              selectedBinId: null
            })
          );
          await displayPickData({
            portId,
            pickId: focusedPick.pickId,
            batchId: focusedPick.batchId,
            gridId
          });
        }
      }
      await verifyPick({
        upcOrSku: buffer,
        pickId: focusedPick.pickId,
        verifyMethod: ScanningVerification.SCANNER,
        workstationId
      })
        .unwrap()
        .catch((err: unknown) => {
          // The verifyPick API returns an error message with text: "The UPC or
          // SKU do not match the pick". This path should only match UPC so we
          // should delete SKU from the error message when a scan error occurs.
          let errorMessage = getMessageFromRtkError(err);
          const stringToReplace = "The UPC or SKU do not match the pick";
          if (errorMessage.includes(stringToReplace))
            errorMessage = errorMessage.replace(
              stringToReplace,
              "The UPC does not match the pick"
            );
          errorToast(errorMessage);
        });
      return matchesUpc(focusedPick.allUpcs, buffer);
    },
    deps: [focusedPick, arePickWeightsReady, isRemovingWeight, ableToPickQty],
    disabled: ap_confirmCartNumberEnabled && !cartNumberConfirmed
  });

  const ptlStyle = {
    color: ternaryIff(
      props.signalr.state === "Connected",
      "success.main",
      "autostoreRed.main"
    ),
    ...(!ap_fusionPortScreenEnabled && {
      display: "inline-block",
      marginLeft: 5
    })
  };

  useEffect(() => {
    setMenuItems([
      {
        textContent: t("refresh"),
        trackingLabel: "Refresh",
        actionCb: () => refetchPickingState()
      },
      {
        textContent: t("check bin status"),
        trackingLabel: "Check Bin Status",
        actionCb: () => {
          binStatusCheck(currentPick?.pickBin?.autostoreBin?.autostoreBinId);
        }
      }
    ]);
  }, [
    refetchPickingState,
    setMenuItems,
    t,
    currentPick?.pickBin?.autostoreBin?.autostoreBinId,
    binStatusCheck
  ]);

  useEffect(() => {
    const batchName = pickingState?.batchName;
    const taskGroupId = pickingState?.taskGroupId;
    setInformationalMenuItems([
      ...(batchName && taskGroupId
        ? [
            {
              textContent: ` ${t("batch")}: ${batchName} `
            },
            {
              textContent: `${t("task group id")}: ${taskGroupId}`
            }
          ]
        : [])
    ]);
  }, [pickingState, setInformationalMenuItems, t]);

  // If the inactivity timer is enabled, we don't want to track it when the SLAM
  // modal is displayed since this workflow is hard to recover from and the user
  //  needs to stay on the picking page while the modal is displayed
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (isScanAndApplyLabelModalShown) {
      clearInactivityTimer();
    }

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!isScanAndApplyLabelModalShown) {
      restartInactivityTimer();
    }
  }, [
    isScanAndApplyLabelModalShown,
    clearInactivityTimer,
    restartInactivityTimer
  ]);

  // Update the batch progress bar once the current batch is finished
  useEffect(() => {
    if (batchCompletedModalOpen && areShipmentsEnabled)
      void refetchGetWaveProgress();
  }, [batchCompletedModalOpen, refetchGetWaveProgress, areShipmentsEnabled]);

  useEffect(() => {
    if (areShipmentsEnabled && isWaveProgressBarError) {
      errorToast(getMessageFromRtkError(waveProgressBarError));
    }
  }, [
    areShipmentsEnabled,
    isWaveProgressBarError,
    waveProgressBarError,
    errorToast
  ]);

  return (
    <ASDSThemeProvider>
      <Grid
        id="AP_main_container"
        container
        spacing={1}
        sx={{
          padding: "40px 5px 5px 5px"
        }}
      >
        <TopActionButtons
          currentPick={currentPick}
          handleFetchNextPickBin={handleFetchNextPickBin}
        />
        <Grid
          id="AP_inner_window"
          item
          xs={12}
          md={12}
          sx={{ display: "flex", width: "100%" }}
        >
          {/* Bin and Product column  */}
          <Grid
            id="AP_bin_product_column"
            item
            xs={4}
            sx={
              totesPlacement === "split"
                ? {
                    position: "absolute",
                    right: 0,
                    margin: "0 auto",
                    left: 0,
                    marginTop: "-40px"
                  }
                : { marginTop: "40px" }
            }
          >
            <PickingBins currentPick={currentPick} />
            <FocusedPickInfoCard
              onRemoveWeight={handleRemoveWeight}
              isRemovingWeight={isRemovingWeight}
            />
          </Grid>
          <AutostorePickingTotes handleToteClick={handleToteClick} />
        </Grid>
        {/* end of pick container */}
      </Grid>
      {/* end of main container */}
      <div
        id="ptl_info_container"
        style={{
          width: ap_fusionPortScreenEnabled ? 100 : 200,
          position: "fixed",
          bottom: 20,
          right: 15,
          textAlign: ap_fusionPortScreenEnabled ? "left" : "right"
        }}
      >
        {t("ptl")}:
        <Typography sx={ptlStyle}>
          {t(
            `${props.signalr.state.toLowerCase()}` as
              | "connected"
              | "disconnected"
          )}
        </Typography>
      </div>
      {(displayTotesPrepModal ||
        displayToteConfirmationModalForSpecificTote) && (
        <TotesPrepModal
          originateTotes={originalTotes}
          restartInactivityTimer={restartInactivityTimer}
        />
      )}
      <StartPickingModal
        open={
          batchCompletedModalOpen &&
          isScanLabelModalHidden &&
          !isRestockPickModalOpen
        }
        onClose={onBatchCompleteModalClose}
        onHomeButtonClickCallback={handleHomeButtonClick}
        onNextPickButtonClickCallback={getNextBatch}
        maxWidth="sm"
        title={t("batch complete")}
        batchName={recentBatchInfo?.batchName}
        text={
          pickingConfigurations?.includes("Manual")
            ? `Move Cart to ${
                recentBatchInfo?.temperatureZone || ""
              } Autostore Staging Area`
            : t("pick next batch")
        }
        pickButtonText={t("start new pick")}
      />
      {outOfStockModalStatus && (
        <AutostoreOutOfStockDialog handleMoveNextPick={handleMoveNextPick} />
      )}
      {isCartNumberConfirmationShown && (
        <CartNumberConfirmationModal
          maxWidth="md"
          resetState={resetStateAndRedirect}
        />
      )}
      {isPickQuantityConfirmationFlowEnabled && (
        <ConfirmPickQuantityModal
          open={confirmPickQuantityModalIsShown}
          maxWidth="lg"
        />
      )}
      {isScanAndApplyLabelModalShown && (
        <ScanAndApplyLabelConfirm
          originalTotes={originalTotes}
          addedTotes={addedTotes}
          restartInactivityTimer={restartInactivityTimer}
        />
      )}
      {ap_scanningIndicatorShown && (
        <ScanningIndicator
          scanState={scanState}
          scannedBarcode={scannedBarcode}
          placeholderText="Scan Product"
          position="left"
        />
      )}
      <AddToteModal />
      {ap_showOrderOrProductCancellationModal && isRestockPickModalOpen && (
        <RestockPickModal
          handleContinuePickingButtonClick={async () => {
            await handleMoveNextPick({
              reporter: "order cancellation modal"
            });
          }}
        />
      )}
    </ASDSThemeProvider>
  );
}

export default connector(AutostorePicking);
